/**
 * glør Varianten-Wahl — eigenständiger Inline-Varianten-Wähler (Größe + Farbe),
 * standardmäßig nur mobil (Sichtbarkeit steuert das CSS via .glr-vq--mobile-only).
 *
 * Update-sicher & DRY (Proxy-Muster aus assets/glr-sticky-buy.js /
 * assets/glr-scent-select.js):
 *  - Klick auf Pill/Swatch steuert den nativen <variant-picker> FERN: passenden
 *    Radio per Options-Name + Wert klicken → native Engine synchronisiert Preis,
 *    Medien und Buy-Buttons der ganzen Seite. Ohne nativen Picker: Form-
 *    Varianten-ID setzen und ProductSelectEvent selbst feuern.
 *  - ProductSelectEvent-Listener (über @shopify/events) hält die aktiven
 *    Swatches synchron, wenn die Variante woanders geändert wird.
 *  - Optimistisches SOFORT-Update beim Klick (synchron, ohne Netzwerk-
 *    Roundtrip): aktive Pill/Swatch markieren, Form-Varianten-ID setzen und
 *    den angezeigten Preis aus der lokalen Matrix schreiben. Der native Fetch
 *    zieht Medien/Buttons danach im Hintergrund nach; ProductSelectEvent
 *    reconciled idempotent (kein Zurückspringen).
 *  - Touch-Prefetch (pointerdown/touchstart) wärmt EXAKT die native Fetch-URL
 *    (?option_values=…) vor → der Klick-Fetch ist ein Browser-Cache-Treffer.
 *  - Dieser Block KAUFT NICHT — er wählt nur die Variante; der Kauf läuft über
 *    den nativen Add-to-Cart.
 */

/** @typedef {{id:number, options:string[], available:boolean, price:string, compare:string}} VqVariant */

class GlrVariantQuick extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId || '';
    this.productUrl = (this.dataset.productUrl || '').split('?')[0];

    /** @type {VqVariant[]} */
    this.matrix = this.#parseMatrix();
    /** @type {Set<string>} IDs echter Vorbestell-Varianten (aus glr-pdp-availability). */
    this.preorderIds = this.#loadPreorderMap();
    this.markerText = this.dataset.glrVqPoText || 'Vorbestellung';
    this.optBtns = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-glr-vq-opt]')));

    this.native = this.#findNativePicker();
    this.#hideNativeIfAsked();

    /** @type {Set<string>} bereits aufgewärmte Prefetch-URLs (kein Doppel-Fetch). */
    this._prefetched = new Set();

    this.#wireOptions();

    // ProductSelectEvent an der Section → aktive Swatches reaktiv nachziehen.
    this.section = this.closest('.shopify-section');
    this.#importAndListen();

    // Verfügbarkeit initial an aktuelle Auswahl angleichen.
    this.#refreshAvailability();
    // Angezeigten Options-Wert im Label initial an aktive Auswahl angleichen.
    this.#updateSelectedLabels(false);
  }

  disconnectedCallback() {
    if (this.section && this._pselName) {
      this.section.removeEventListener(this._pselName, this.#onProductSelect);
    }
  }

  /** @returns {VqVariant[]} */
  #parseMatrix() {
    const el = this.querySelector('[data-glr-vq-variants]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent || '[]') || [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Liest die minimale Preorder-Map, die glr-pdp-availability rendert
   * (`<script data-glr-preorder-map>` mit `{ <variantId>: true }`). Wird NICHT
   * neu berechnet — ist der Block nicht vorhanden, gibt es schlicht keine Marker.
   * @returns {Set<string>}
   */
  #loadPreorderMap() {
    const set = new Set();
    const el = document.querySelector('[data-glr-preorder-map]');
    if (!el) return set;
    try {
      const map = JSON.parse(el.textContent || '{}') || {};
      for (const id in map) {
        if (map[id]) set.add(String(id));
      }
    } catch (e) {
      /* defekte Map ignorieren → keine Marker */
    }
    return set;
  }

  /**
   * Setzt den Preorder-Marker je Optionswert dynamisch: markiert genau die
   * Werte, deren resultierende Variante (dieser Wert + aktuell gewaehlte andere
   * Optionen) wirklich Vorbestellung ist. Verfuegbare bekommen keinen Marker,
   * ausverkaufte bleiben unangetastet. Idempotent.
   */
  #refreshPreorderMarkers() {
    const wantsStock = this.hasAttribute('data-glr-vq-stock');
    const hasPreorder = !!(this.preorderIds && this.preorderIds.size);
    // Ohne Vorbestell-Varianten UND ohne Verfuegbarkeits-Punkt gibt es nichts
    // zu setzen. Frueher lag diese Bedingung nur auf der Preorder-Map - der
    // gruene Punkt waere dann bei Produkten ohne Vorbestellung nie erschienen.
    if (!hasPreorder && !wantsStock) return;

    const active = this.#activeValues();
    let anyPre = false;
    let anyAv = false;

    // Punkte nur an den Optionen, die im Customizer freigegeben sind.
    // 'all' (oder fehlend) = alle Optionen, wie bisher.
    const rawIdx = this.dataset.glrVqMarkerIdx;
    const allowAll = !rawIdx || rawIdx === 'all';
    const allowed = allowAll
      ? null
      : new Set(rawIdx.split(',').filter((x) => x !== '').map(Number));

    for (const b of this.optBtns) {
      const i = Number(b.dataset.optIndex);
      if (allowed && !allowed.has(i)) {
        b.classList.remove('is-preorder', 'is-instock');
        this.#setMarker(b, 'data-glr-vq-po', 'glr-vq__po', false, this.markerText);
        this.#setMarker(b, 'data-glr-vq-av', 'glr-vq__av', false, '');
        continue;
      }
      const val = b.dataset.optionValue || '';
      const variant = this.matrix.find(
        (v) => v.options[i] === val && active.every((av, j) => j === i || av == null || v.options[j] === av)
      );
      const soldout = b.classList.contains('is-soldout');
      // Marker nur bei echter Preorder-Variante — und nie an ausverkauften Werten.
      const pre = hasPreorder && !!(variant && this.preorderIds.has(String(variant.id))) && !soldout;
      // Gruen genau dann, wenn es die Variante gibt, sie nicht vorbestellt und
      // nicht ausverkauft ist. Beides zusammen kann ein Wert nie sein.
      const av = wantsStock && !!variant && !pre && !soldout;

      if (pre) anyPre = true;
      if (av) anyAv = true;

      b.classList.toggle('is-preorder', pre);
      b.classList.toggle('is-instock', av);
      this.#setMarker(b, 'data-glr-vq-po', 'glr-vq__po', pre, this.markerText);
      this.#setMarker(b, 'data-glr-vq-av', 'glr-vq__av', av, '');
    }

    // Die Hinweiszeilen tragen den Text, den frueher das Badge im Pill trug.
    // Jede erscheint nur, wenn bei der aktuellen Auswahl mindestens ein Wert
    // in diesen Zustand faellt.
    const note = this.querySelector('[data-glr-vq-po-note]');
    if (note) note.hidden = !anyPre;
    const avNote = this.querySelector('[data-glr-vq-av-note]');
    if (avNote) avNote.hidden = !anyAv;
  }

  /**
   * Setzt oder entfernt einen Punkt am Optionswert. Idempotent.
   * @param {HTMLElement} btn
   * @param {string} attr
   * @param {string} cls
   * @param {boolean} on
   * @param {string} label
   */
  #setMarker(btn, attr, cls, on, label) {
    let m = btn.querySelector('[' + attr + ']');
    if (on) {
      if (!m) {
        m = document.createElement('span');
        m.className = cls;
        m.setAttribute(attr, '');
        m.textContent = label;
        btn.appendChild(m);
      }
    } else if (m) {
      m.remove();
    }
  }

  /** Registriert den ProductSelectEvent-Listener über @shopify/events (echter Name). */
  async #importAndListen() {
    try {
      const { StandardEvents } = await import('@shopify/events');
      if (this.section && StandardEvents?.productSelect) {
        this._pselName = StandardEvents.productSelect;
        this.section.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
      }
    } catch (e) {
      /* events nicht verfügbar — kein reaktiver Reconcile, Proxy funktioniert trotzdem */
    }
  }

  /**
   * Blendet den nativen <variant-picker> aus, wenn dieser Block ihn ersetzt
   * (Setting "Nativen Varianten-Picker ausblenden", Darstellung "Karten").
   * Ohne das Setting bleibt alles wie bisher: der native Picker bleibt stehen
   * und dieser Block ist nur die zusaetzliche Schnellwahl unter dem Bild.
   */
  #hideNativeIfAsked() {
    if (!this.hasAttribute('data-glr-vq-hide-native')) return;
    if (this.native instanceof HTMLElement) this.native.style.display = 'none';
  }

  /** @returns {Element|null} */
  #findNativePicker() {
    const pickers = Array.from(document.querySelectorAll('variant-picker'));
    if (pickers.length === 0) return null;
    const byId = pickers.find((p) => p instanceof HTMLElement && p.dataset.productId === this.productId);
    return byId || pickers[0];
  }

  // --- Optionen (Proxy) ------------------------------------------------------

  #wireOptions() {
    for (const btn of this.optBtns) {
      // Klick = tatsächliche Auswahl (bleibt auf click/pointerup).
      btn.addEventListener('click', () => this.#selectOption(btn));
      // Früh (schon beim Fingerkontakt) die native Ziel-URL vorwärmen, damit der
      // anschließende native Klick-Fetch aus dem Browser-Cache bedient wird.
      // Passiv → blockiert das Scrollen/Tippen nicht. pointerdown deckt Maus +
      // Touch + Pen ab; touchstart als Fallback (per URL im Set dedupliziert).
      btn.addEventListener('pointerdown', () => this.#prefetchFor(btn), { passive: true });
      btn.addEventListener('touchstart', () => this.#prefetchFor(btn), { passive: true });
    }
  }

  /**
   * Wärmt die Ziel-URL des nativen Varianten-Switch vor (Cache-Warming), OHNE
   * DOM-Eingriff. Bestimmt aus dem NATIVEN <variant-picker> die passenden
   * Options-Radios (data-option-value-id) für die prospektive Auswahl und baut
   * dieselbe ?option_values=…-URL wie variant-picker.js#buildRequestUrl.
   * @param {HTMLElement} btn
   */
  #prefetchFor(btn) {
    if (btn.classList.contains('is-soldout')) return;
    if (!this.native) this.native = this.#findNativePicker();
    const radio = this.#findNativeRadio(btn.dataset.optionName || '', btn.dataset.optionValue || '');
    if (!radio || radio.checked) return;
    const url = this.#buildPrefetchUrl(radio);
    if (!url || this._prefetched.has(url)) return;
    this._prefetched.add(url);
    fetch(url, { credentials: 'same-origin', priority: 'low' }).catch(() => {});
  }

  /**
   * Findet im nativen Picker das Radio-Input für Options-Name + Wert.
   * @param {string} optName
   * @param {string} value
   * @returns {HTMLInputElement|null}
   */
  #findNativeRadio(optName, value) {
    if (!this.native) return null;
    const wantName = optName.trim().toLowerCase();
    const wantVal = value.trim().toLowerCase();
    const radios = Array.from(this.native.querySelectorAll('input[type="radio"]'));
    let radio = radios.find(
      (r) =>
        r instanceof HTMLInputElement &&
        (r.dataset.optionName || '').trim().toLowerCase() === wantName &&
        r.value.trim().toLowerCase() === wantVal
    );
    if (!radio) {
      radio = radios.find((r) => r instanceof HTMLInputElement && r.value.trim().toLowerCase() === wantVal);
    }
    return radio instanceof HTMLInputElement ? radio : null;
  }

  /**
   * Baut die Prefetch-URL nach dem Muster von variant-picker.js: pro Fieldset die
   * aktuell gewählte Wert-ID, für das Fieldset der prospektiven Auswahl dessen ID.
   * @param {HTMLInputElement} radio
   * @returns {string}
   */
  #buildPrefetchUrl(radio) {
    if (!this.native) return '';
    const fieldsets = Array.from(this.native.querySelectorAll('fieldset'));
    const radioFs = radio.closest('fieldset');
    const values = [];
    if (fieldsets.length) {
      for (const fs of fieldsets) {
        const r = fs === radioFs ? radio : fs.querySelector('input[type="radio"]:checked');
        const id = r instanceof HTMLElement ? r.dataset.optionValueId : '';
        if (id) values.push(id);
      }
    } else if (radio.dataset.optionValueId) {
      values.push(radio.dataset.optionValueId);
    }
    if (!values.length) return '';
    // EXAKT wie variant-picker.js#buildRequestUrl bauen (gleiche Basis, gleiche
    // Param-Reihenfolge, KEIN encodeURIComponent bei view) → identische URL, damit
    // der native Klick-Fetch aus dem Prefetch-Cache bedient wird (Cache-Treffer).
    const base = (this.native instanceof HTMLElement && this.native.dataset.productUrl) || this.productUrl;
    if (!base) return '';
    const params = [];
    const view = new URLSearchParams(location.search).get('view');
    if (view) params.push('view=' + view);
    params.push('option_values=' + values.join(','));
    return base + '?' + params.join('&');
  }

  /** @param {HTMLElement} btn */
  #selectOption(btn) {
    // Ausverkaufte Kombis nicht wählbar.
    if (btn.classList.contains('is-soldout')) return;

    const optIndex = Number(btn.dataset.optIndex);
    const value = btn.dataset.optionValue || '';
    const optName = btn.dataset.optionName || '';
    const variantId = btn.dataset.variantId || '';

    // Optimistisch aktive Markierung setzen (kein Flackern).
    this.#setActiveInGroup(optIndex, value);
    // Angezeigten Wert im Options-Label live nachziehen (mit dezentem Fade).
    this.#updateSelectedLabels(true);

    // 1) OPTIMISTISCH: passende Variante aus lokaler Matrix bestimmen und die
    //    Form-Varianten-ID direkt setzen (Kauf-Garantie), ohne auf den nativen
    //    Roundtrip zu warten.
    const optimistic = this.#variantFromActive();
    if (optimistic) {
      this.#setFormVariant(String(optimistic.id));
      // Preis SOFORT aus der lokalen Matrix schreiben (nicht auf den Fetch warten).
      this.#updatePrice(optimistic);
    } else if (variantId) {
      this.#setFormVariant(variantId);
    }

    // 2) Nativen Picker fernsteuern → volle native Kette (Preis/Medien/Buttons).
    const drove = this.#driveNative(optName, value);

    // 3) Ohne nativen Picker: ProductSelectEvent selbst feuern (Reconciliation).
    if (!drove) {
      const target = optimistic || (variantId ? { id: Number(variantId) } : null);
      if (target) this.#dispatchProductSelect(target.id);
    }

    // Verfügbarkeit der anderen Gruppe(n) an die neue Auswahl angleichen.
    this.#refreshAvailability();

    // Nativer Picker könnte neu gerendert werden.
    this.native = this.#findNativePicker();
    this.#hideNativeIfAsked();
  }

  /**
   * Schaltet den nativen Picker per Options-Name + Wert um.
   * @param {string} optName
   * @param {string} value
   * @returns {boolean}
   */
  #driveNative(optName, value) {
    if (!this.native) return false;
    const wantVal = value.trim().toLowerCase();

    // Erst per Options-Name + Wert (robust gegen Wert-Kollisionen zwischen Optionen).
    const radio = this.#findNativeRadio(optName, value);
    if (radio instanceof HTMLInputElement) {
      // Der native Klick-Fetch nutzt die bei pointerdown gewärmte Cache-URL.
      if (!radio.checked) radio.click();
      return true;
    }

    // Dropdown-Variante des nativen Pickers.
    const select = this.native.querySelector('select');
    if (select instanceof HTMLSelectElement) {
      const opt = Array.from(select.options).find(
        (o) => o.value.trim().toLowerCase() === wantVal || o.textContent.trim().toLowerCase() === wantVal
      );
      if (opt) {
        select.value = opt.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    return false;
  }

  /**
   * Adresse, unter der die Seite fuer eine andere Variante nachgeladen wird.
   *
   * Entscheidend ist `view`: liegt das Produkt auf einer ALTERNATIVEN Vorlage
   * (…/products/x?view=la-vik-neu), liefert `/products/x?variant=…` die
   * STANDARD-Vorlage zurueck. Darin fehlen alle Bloecke der alternativen
   * Vorlage - der Bundle-Block findet sich im frischen HTML nicht wieder und
   * aktualisiert deshalb nichts. Auf der Standardvorlage fiel das nie auf,
   * weil dort beide Adressen dasselbe liefern.
   *
   * Genauso muessen die Vorschau-Parameter erhalten bleiben, sonst kommt die
   * Antwort aus dem veroeffentlichten statt aus dem Vorschau-Theme.
   *
   * @param {number|string} variantId
   * @returns {string}
   */
  #renderUrl(variantId) {
    const base = new URL(this.productUrl, window.location.origin);
    const here = new URL(window.location.href);

    // Nur uebernehmen, wenn wir wirklich auf dieser Produktseite stehen -
    // sonst schleppen wir fremde Parameter mit.
    if (here.pathname === base.pathname) {
      for (const key of ['view', 'preview_theme_id', '_fd', 'pb', '_ab', '_bt']) {
        const value = here.searchParams.get(key);
        if (value !== null) base.searchParams.set(key, value);
      }
    }
    base.searchParams.set('variant', String(variantId));
    return base.toString();
  }

  /**
   * Ohne nativen Picker: ProductSelectEvent feuern (Muster aus scent-select).
   * @param {number} variantId
   */
  async #dispatchProductSelect(variantId) {
    if (!this.productUrl) return;
    let ProductSelectEvent;
    try {
      ({ ProductSelectEvent } = await import('@shopify/events'));
    } catch (e) {
      return;
    }
    const deferred = ProductSelectEvent.createPromise();
    this.dispatchEvent(
      new ProductSelectEvent({
        product: { id: this.productId },
        selectedOptions: [],
        detail: {},
        promise: deferred.promise,
      })
    );
    try {
      const res = await fetch(this.#renderUrl(variantId));
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      deferred.resolve({
        variant: null,
        detail: { html, productId: this.productId, resource: { id: variantId } },
      });
    } catch (err) {
      deferred.reject?.(err);
    }
  }

  /**
   * Setzt die Varianten-ID in allen Warenkorb-Formularen dieses Produkts.
   * @param {string} variantId
   */
  #setFormVariant(variantId) {
    const inputs = new Set();
    document.querySelectorAll('product-form-component').forEach((pf) => {
      if (!(pf instanceof HTMLElement)) return;
      if (pf.dataset.productId && pf.dataset.productId !== this.productId) return;
      pf.querySelectorAll('[name="id"]').forEach((i) => inputs.add(i));
    });
    if (inputs.size === 0) {
      document.querySelectorAll('form[action*="/cart/add"] [name="id"]').forEach((i) => inputs.add(i));
    }
    inputs.forEach((input) => {
      if (input instanceof HTMLInputElement && input.value !== variantId) {
        input.value = variantId;
        // KEIN natives `change` mehr: daran haengt eine Zuhoerer-Kette von
        // Shopify (fetcher-subscriptions), die einen Produktkontext erwartet,
        // den ein selbst erzeugtes Ereignis nicht mitbringt - sie warf bei
        // JEDEM Variantenklick einen TypeError in die Konsole. Fuer den
        // Warenkorb genuegt der gesetzte Wert; die Seite wird ohnehin ueber
        // ProductSelectEvent aktualisiert. Eigenes Ereignis fuer eigene
        // Zuhoerer, damit nichts Fremdes darauf anspringt.
        input.dispatchEvent(new CustomEvent('glr:variant', { bubbles: true, detail: { variantId } }));
      }
    });
  }

  /**
   * Aktualisiert den ANGEZEIGTEN Preis der Horizon-Preis-Elemente SOFORT aus der
   * lokalen Matrix — noch bevor der native Fetch zurückkommt. Der native
   * ProductSelectEvent ersetzt den priceContainer danach idempotent mit dem echten
   * HTML (kein Zurückspringen). Reines Schreiben (textContent/Klassen), kein Read
   * dazwischen → kein Layout-Thrash.
   * @param {VqVariant} variant
   */
  #updatePrice(variant) {
    if (!variant || !variant.price) return;
    const hasCompare = Boolean(variant.compare);
    for (const pp of this.#priceElements()) {
      const container = pp.querySelector('[ref="priceContainer"]') || pp;
      // Aktueller Preis: alle .price-Spans außer der Streichpreis-Spans.
      container.querySelectorAll('.price').forEach((el) => {
        if (!el.classList.contains('compare-at-price')) el.textContent = variant.price;
      });
      // Streichpreis nur setzen, wenn vorhanden (sonst bleibt alter Wert unsichtbar).
      if (hasCompare) {
        container.querySelectorAll('.compare-at-price').forEach((el) => {
          el.textContent = variant.compare;
        });
      }
      // Regulär-/Sale-Layout umschalten (wie im Preis-Snippet: price__hidden).
      const regular = container.querySelector('.price__regular');
      const sale = container.querySelector('.price__sale');
      if (regular && sale) {
        regular.classList.toggle('price__hidden', hasCompare);
        sale.classList.toggle('price__hidden', !hasCompare);
      }
    }
  }

  /**
   * Sichtbare Horizon-Preis-Elemente dieses Produkts (ohne product-card-Kontext).
   * @returns {HTMLElement[]}
   */
  #priceElements() {
    return /** @type {HTMLElement[]} */ (
      Array.from(document.querySelectorAll('product-price')).filter((pp) => {
        if (!(pp instanceof HTMLElement)) return false;
        if (pp.closest('product-card')) return false;
        if (this.productId && pp.dataset.productId && pp.dataset.productId !== this.productId) return false;
        return true;
      })
    );
  }

  /** @param {number} optIndex @param {string} value */
  #setActiveInGroup(optIndex, value) {
    const want = value.trim().toLowerCase();
    for (const b of this.optBtns) {
      if (Number(b.dataset.optIndex) !== optIndex) continue;
      const active = (b.dataset.optionValue || '').trim().toLowerCase() === want;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-checked', active ? 'true' : 'false');
    }
  }

  /** Aktive Optionswerte je Gruppe (aus den Buttons). @returns {(string|null)[]} */
  #activeValues() {
    const byIndex = {};
    for (const b of this.optBtns) {
      const i = Number(b.dataset.optIndex);
      if (b.classList.contains('is-active')) byIndex[i] = b.dataset.optionValue || '';
    }
    const maxIdx = Math.max(-1, ...this.optBtns.map((b) => Number(b.dataset.optIndex)));
    const vals = [];
    for (let i = 0; i <= maxIdx; i++) vals.push(byIndex[i] ?? null);
    return vals;
  }

  /** @returns {VqVariant|undefined} */
  #variantFromActive() {
    const vals = this.#activeValues();
    return this.matrix.find((v) => vals.every((val, i) => val == null || v.options[i] === val));
  }

  /**
   * Graut Optionswerte aus, die mit der aktuellen Auswahl der ANDEREN Gruppen
   * in KEINER verfügbaren Variante vorkommen (auk-Stil). Idempotent.
   */
  #refreshAvailability() {
    if (!this.matrix.length) return;
    const active = this.#activeValues();
    for (const b of this.optBtns) {
      const i = Number(b.dataset.optIndex);
      const val = b.dataset.optionValue || '';
      // Verfügbar, wenn es eine verfügbare Variante gibt, in der DIESER Wert steht
      // und alle anderen aktiven Optionen passen.
      const ok = this.matrix.some((v) => {
        if (!v.available) return false;
        if (v.options[i] !== val) return false;
        return active.every((av, j) => j === i || av == null || v.options[j] === av);
      });
      b.classList.toggle('is-soldout', !ok);
      // Aktive Auswahl nie sperren.
      if (b.classList.contains('is-active')) b.classList.remove('is-soldout');
    }
    // Preorder-Marker an die neue Verfuegbarkeit angleichen (nach is-soldout).
    this.#refreshPreorderMarkers();
    this.#refreshImages();
  }

  /**
   * Zieht die Bilder der Optionskacheln an die aktuelle Auswahl nach.
   *
   * WARUM: Die Kacheln rendert Liquid einmal beim Seitenaufbau. Beim
   * Variantenwechsel ersetzt Horizon nur seine eigenen Bereiche (Preis,
   * Galerie, Kaufen-Knopf) - dieser Block bleibt stehen. Steht "Variantenbild
   * statt Farbfeld" an, zeigten die Farbkacheln deshalb weiter das Bild der
   * zuerst gerenderten Groesse. Die Adressen liegen in der Variantenmatrix,
   * ein Nachladen ist also nicht noetig.
   */
  #refreshImages() {
    if (!this.hasAttribute('data-glr-vq-variant-image')) return;
    const active = this.#activeValues();
    for (const b of this.optBtns) {
      const img = b.querySelector('img');
      if (!img) continue;
      const i = Number(b.dataset.optIndex);
      const val = b.dataset.optionValue || '';
      // Variante, die diesen Wert traegt und sonst zur aktiven Auswahl passt.
      const match =
        this.matrix.find(
          (v) => v.options[i] === val && active.every((av, j) => j === i || av == null || v.options[j] === av)
        ) || this.matrix.find((v) => v.options[i] === val);
      if (!match || !match.image) continue;
      if (img.getAttribute('src') === match.image) continue;
      img.setAttribute('src', match.image);
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
    }
  }

  // --- Reaktivität -----------------------------------------------------------

  /** @param {Event} event */
  #onProductSelect = (event) => {
    const t = event.target;
    if (t instanceof Element && t.closest('product-card')) return;
    requestAnimationFrame(() => this.#reflectVariant(this.#currentVariant()));
  };

  /** Aktuelle Variante: erst Form-ID, sonst aus aktiven Optionen. @returns {VqVariant|undefined} */
  #currentVariant() {
    const id = this.#formVariantId();
    if (id) {
      const byId = this.matrix.find((v) => String(v.id) === id);
      if (byId) return byId;
    }
    return this.#variantFromActive() || this.matrix.find((v) => v.available) || this.matrix[0];
  }

  /** @returns {string} */
  #formVariantId() {
    let input = null;
    document.querySelectorAll('product-form-component').forEach((pf) => {
      if (input || !(pf instanceof HTMLElement)) return;
      if (pf.dataset.productId && pf.dataset.productId !== this.productId) return;
      const i = pf.querySelector('[name="id"]');
      if (i instanceof HTMLInputElement) input = i;
    });
    if (!input) {
      const i = document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (i instanceof HTMLInputElement) input = i;
    }
    return input ? input.value : '';
  }

  /** @param {VqVariant|undefined} variant */
  #reflectVariant(variant) {
    if (!variant) return;
    for (const b of this.optBtns) {
      const i = Number(b.dataset.optIndex);
      const active = (b.dataset.optionValue || '') === variant.options[i];
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-checked', active ? 'true' : 'false');
    }
    this.#refreshAvailability();
    this.#updateSelectedLabels(true);
  }

  /**
   * Zieht den angezeigten Options-Wert im Label (`[data-glr-vq-selected-label]`)
   * an die aktuell aktive Auswahl nach — wie der native Horizon-Picker (Name +
   * gewählter Wert). @param {boolean} animate Bei true kurzes Fade beim Wechsel.
   */
  #updateSelectedLabels(animate) {
    const active = this.#activeValues();
    this.querySelectorAll('[data-glr-vq-selected-label]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const i = Number(el.dataset.optIndex);
      const val = active[i];
      if (val == null) return;
      if (el.textContent === val) return;
      el.textContent = val;
      if (animate) this.#pulseLabel(el);
    });
  }

  /**
   * Dezentes Fade beim Werte-Wechsel (respektiert prefers-reduced-motion).
   * @param {HTMLElement} el
   */
  #pulseLabel(el) {
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    } catch (e) {
      /* matchMedia nicht verfügbar → einfach animieren */
    }
    el.classList.remove('is-updating');
    // Reflow erzwingen, damit die Animation neu startet.
    void el.offsetWidth;
    el.classList.add('is-updating');
  }
}

if (!customElements.get('glr-variant-quick')) {
  customElements.define('glr-variant-quick', GlrVariantQuick);
}
